import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import useExpress, { expressMutation } from '../../repository/react-query/useExpress';
import { restoreCache, setDeletedQueryCache, updateQueryCache } from '../optimistic-update-utils';

const baseEndpoint = '/bay';

const getBaysQuery = ({ path, includeDeleted, queryParams }) => {
    const { where = {}, ...rest } = queryParams;
    const params = {
        where: {
            ...(includeDeleted ? { deleted_at: 'any' } : { deleted_at: { eq: null } }),
            ...where,
        },
        ...rest,
    };

    const endpoint = path ? `${baseEndpoint}/${path}` : baseEndpoint;
    return useExpress({ endpoint, params });
};

export const useFetchBays = ({ path, includeDeleted, queryParams = {}, ...useQueryOptions }) => {
    const queryFn = getBaysQuery({ path, includeDeleted, queryParams });
    const queryKey = ['bay-data', { includeDeleted, ...queryParams }];
    return useQuery({ queryKey, queryFn, ...useQueryOptions });
};

export const useCreateBay = ({ key }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data }) => {
            return expressMutation({
                method: 'POST',
                endpoint: `${baseEndpoint}`,
                body: data,
            });
        },
        onSuccess: () => {
            if (key) {
                queryClient.invalidateQueries({ queryKey: [key] });
            }
        },
    });
};

export const useUpdateBay = ({ key }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => {
            return expressMutation({
                method: 'PATCH',
                endpoint: `${baseEndpoint}/${id}`,
                body: data,
            });
        },
        onMutate: (updatedItem) => updateQueryCache(queryClient, [key], { id: updatedItem.id, ...updatedItem.data }),
        onError: (_err, _, ctx) => restoreCache(queryClient, ctx),
        onSuccess: () => {
            if (key) {
                queryClient.invalidateQueries({ queryKey: [key] });
            }
        },
    });
};

export const useDeleteBay = ({ key }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id }) => {
            return expressMutation({
                method: 'DELETE',
                endpoint: `${baseEndpoint}/${id}`,
            });
        },
        onMutate: (updatedItem) => setDeletedQueryCache(queryClient, [key], updatedItem),
        onError: (_err, _, ctx) => restoreCache(queryClient, ctx),
        onSuccess: () => {
            if (key) {
                queryClient.invalidateQueries({ queryKey: [key] });
            }
        },
    });
};
